<template>
  <main-container class="model-detail-container" title="モデル詳細">
    <h3 slot="sub-title" class="sub-title" style="position: relative;">{{modelName}}<br><span
            style="font-size: 0.7rem; line-height: 1; color: #888; position: absolute; bottom: -4px;">{{modelId}}</span>
    </h3>
    <div slot="main-content" v-show="modelInfo && loaded">
      <div style="height: 100%; width: 1080px;">

        <el-tabs v-model="activeTabName" :stretch="true">

          <el-tab-pane v-if="hasEvaluations()"
                       :label="modelInfo.categoryId !== 'UNS' ? '性能' : '集計'"
                       name="evaluations"
          >
            <evaluations-container :evaluations="modelInfo.evaluations"
                                   :comparisons="modelInfo.comparisons"
                                   :yName="modelInfo.yName"
                                   :categoryId="modelInfo.categoryId"></evaluations-container>
          </el-tab-pane>

          <el-tab-pane v-if="hasInfluences()" label="項目の影響度" name="influences">
            <h3>項目の影響度<tooltip-icon :content="influenceTitleTip"></tooltip-icon></h3>
            <div class="influences-container">
              <el-card shadow="never"
                       class="influences-card influences-chart-card">
                <influences :influences="modelInfo.influences"></influences>
              </el-card>
            </div>
          </el-tab-pane>

          <el-tab-pane label="モデルサマリ" name="summary">
            <h3>モデルサマリ</h3>
            <div class="summary-container">
              <el-card shadow="never" class="summary-card"
                       v-if="modelInfo.samples">
                <div class="summary-card-name">全データ</div>
                <div class="summary-card-value">{{modelInfo.samples | number_format}}件
                </div>
              </el-card>
              <template v-if="modelInfo.type !== 'sx'">
                <el-card shadow="never" class="summary-card">
                  <div class="summary-card-name">学習データ</div>
                  <div class="summary-card-value" v-if="!isAllDataModel()">
                    {{modelInfo.samplesTrain |
                    number_format}}件（{{modelInfo.trainRate | percentage}}%）
                    <div v-if="!disabledAllDataModel()">
                      <el-button type="info" size="small"
                                 @click="onClickAllDataModel">全データモデルの作成
                      </el-button>
                    </div>
                  </div>
                  <div class="summary-card-value" v-else>{{modelInfo.samples |
                    number_format}}件（100%）<br>
                    <tooltip-icon placeholder="top" icon="el-icon-info"
                                  style="color: #6ba7ef;"
                                  :content="samplesTrainForAllDataModelTip"></tooltip-icon>
                  </div>
                </el-card>
                <el-card shadow="never" class="summary-card">
                  <div class="summary-card-name">評価データ</div>
                  <div v-if="modelInfo.samples === modelInfo.samplesTrain"
                       class="summary-card-value">学習データと同一
                  </div>
                  <div v-else class="summary-card-value">{{modelInfo.samples -
                    modelInfo.samplesTrain | number_format}}件（{{1
                    - modelInfo.trainRate | percentage}}%）
                    <div v-if="modelInfo.remarks.evaluationData">
                      <el-button type="info" size="small"
                                 @click="onClickEvaluationDataDownload">ダウンロード
                      </el-button>
                    </div>
                  </div>
                </el-card>
              </template>
              <el-card shadow="never" class="summary-card"
                       v-if="modelInfo.yName">
                <div class="summary-card-name">{{modelInfo.categoryId !== 'UNS' ? '予測対象' : '集計対象'}}</div>
                <div class="summary-card-value">{{modelInfo.yName}}</div>
              </el-card>
              <template v-if="modelInfo.type !== 'sx'">
                <el-card
                  v-if="modelInfo.yClassSizes && modelInfo.yClassSizes.length > 0"
                  :shadow="isMultiClassModel() ? 'hover': 'never'"
                  class="summary-card"
                  @click.native="onClickYClass">
                  <div class="summary-card-name">予測対象の割合</div>
                  <div class="summary-card-value"
                       style="text-align: left; line-height: 1.8;">
                    <div v-for="(yClass, index) in modelInfo.yClasses.slice(0, 2)" style="display: flex; flex-direction: row;">
                      <span style="max-width: 90px; flex-shrink: 0; flex-grow:0; white-space:nowrap; text-overflow: ellipsis; overflow:hidden; margin-right: 10px;">{{yClass}}</span>= <span style="width: 60px; text-align: right;">{{modelInfo.yClassSizes[index] | percentage(1)}}%</span>
                    </div>
                    <div v-if="isMultiClassModel()" style="display: flex; flex-direction: row;">…</div>
                  </div>
                  <i v-if="isMultiClassModel()"
                     class="el-icon-zoom-in zoom"></i>
                  <el-dialog
                    custom-class="compare-dialog dialog-size-large tab-dialog"
                    title="予測対象の割合"
                    :append-to-body="true" :visible="visibleYClassDialog"
                    @close="visibleYClassDialog=false"
                    top="0" :modal="false">
                    <y-classes :y-classes="modelInfo.yClasses"
                               :y-class-sizes="modelInfo.yClassSizes"></y-classes>
                  </el-dialog>
                </el-card>
              </template>
            </div>
            <div class="summary-hyper-params-container">
              <el-card shadow="never" class="summary-card">
                <div class="summary-card-name">アルゴリズム</div>
                <div class="summary-card-value" style="line-height: 1.8;">
                  {{modelInfo.algorithmName}}
                  (ver.{{modelInfo.algorithmVersion}}) <span
                  v-if="modelInfo.remarks && modelInfo.remarks.naviType">（自動選択）</span>
                </div>
                <tooltip-icon class="summary-card-tip"
                              :content="algorithmTip"></tooltip-icon>
              </el-card>
              <el-card shadow="never" class="summary-card">
                <div class="summary-card-name">データ変換<br>パッケージ</div>
                <div class="summary-card-value">
                  {{getTransformerPackageName(modelInfo.remarks.transformerPackageId)}}
                </div>
              </el-card>
              <el-card v-for="hyperParam in hyperParams" class="summary-card"
                       shadow="never">
                <div class="summary-card-name">
                  {{hyperParam.summary.hyperParamName}}
                </div>
                <div class="summary-card-value">
                  {{formatHyperParamValue(hyperParam.hyperParam)}}
                </div>
              </el-card>
            </div>
          </el-tab-pane>


          <template v-if="modelInfo.type !== 'sx'">
            <el-tab-pane label="学習データのサマリ" name="figure">
              <h3>学習データのサマリ</h3>
                <div v-if="!modelInfo.figureDetailId" style="text-align: center; padding: 0; display: flex; height: 100%; justify-content: center; align-items: center;">
                  <span>
                    <el-button type="info" class="large" @click="onClickConfirmFigure">表示</el-button>
                  </span>
                </div>
                <div v-else>
                  <template v-if="figureDetail && figureDetail.figurePreview.dist">
                    <figure-container v-if="figureDetail"
                                      :categoryId="modelInfo.categoryId"
                                      :figure="figureDetail"
                                      :yName="modelInfo.yName"
                                      :initialColumnIndex="currentFigurePage"></figure-container>
                  </template>
                  <template v-else-if="figureDetail && figureDetail.figurePreview.acf">
                    <figure-container v-if="figureDetail"
                                      :categoryId="modelInfo.categoryId"
                                      :figure="figureDetail"
                                      :yName="modelInfo.yName"></figure-container>
                  </template>
                </div>
            </el-tab-pane>

          </template>
        </el-tabs>

      </div>
    </div>

    <template slot="main-footer">
      <template v-if="modelInfo">
        <el-button v-if="modelInfo.type !== 'sx'" type="primary" @click="onClickModelUpdate">モデルの更新</el-button>
        <el-button type="primary" @click="onClickModelPredict">モデルの適用</el-button>

        <el-button v-if="modelInfo.type !== 'sx'" type="info" @click="onClickStructureDownload"
                   :disabled="!modelInfo || !modelInfo.structure">モデル情報のダウンロード
        </el-button>
        <el-button v-if="modelInfo.type !== 'sx' && modelInfo.ds.uploadFiles" type="info" @click="onClickModelCopy">
          モデルのコピー
        </el-button>

        <el-button v-if="modelInfo.type !== 'sx'" type="danger" class="delete-button" @click="onClickDelete">
          <font-awesome-icon class="delete icon" icon="trash-alt"/>
        </el-button>
      </template>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../base'
import { ModelDetailInfo } from '../../defines/model/model'
import WebApiClient from '../../api/web'
import { Code } from '../../api/base'

import FigureCmp from '../../components/model/figure/figure.vue'
import EvaluationsCmp from '../../components/model/evaluations.vue'
import InfluensecCmp from '../../components/model/detail/influences.vue'
import DistChartCmp from '../../components/chart/dist.vue'
import AcfChartCmp from '../../components/chart/acf.vue'
import YClassCmp from '../../components/model/detail/y_class.vue'

import { Figure } from '../../defines/model/figure'
import {TRANSFORMER_PACKAGE_MAP} from '../../defines/model/transformer-package'
import FormatUtil from '../../utils/format'
import CommonUtil from '../../utils/common'
import MessageBox from '../../utils/message-box'
import { Category } from '../../defines/const'
import StorageUtil from '../../utils/storage'

const INFLUENCE_LINEAR_TITLE_TIP = `モデルの数式における、各項目の係数を表します。
                                                    その絶対値が大きい項目ほど影響度が高いと言えます。`

const INFLUENCE_DECISION_TREE_TITLE_TIP = `決定木の生成時に、各項目がどれだけ考慮されたかを表します。
                                                           その値が大きい項目ほど影響度が高いと言えます。`

const ALGORITHM_TIP = `モデルを作成する手順のことです。
                                        アルゴリズムにより、生成されるモデルの形や精度が異なります。`

export default {
  name: 'ModelDetailView',
  mixins: [BaseView],
  components: {
    'evaluations-container': EvaluationsCmp,
    'influences': InfluensecCmp,
    'dist-chart': DistChartCmp,
    'acf-chart': AcfChartCmp,
    'figure-container': FigureCmp,
    'y-classes': YClassCmp
  },
  props: {
    modelId: String
  },
  data () {
    return {
      modelName: '',
      loaded: false,
      activeTabName: '',
      modelInfo: ModelDetailInfo,
      visibleInfluencesDialog: false,
      figureDetail: Figure,
      visibleFigureDetail: false,
      visibleYClassDialog: false,
      hyperParams: [],
      currentFigurePage: 0
    }
  },
  computed: {
    influenceLinearTitleTip () {
      return INFLUENCE_LINEAR_TITLE_TIP
    },
    influenceDecisionTreeTitleTip () {
      return INFLUENCE_DECISION_TREE_TITLE_TIP
    },
    algorithmTip () {
      return ALGORITHM_TIP
    },
    influenceTitleTip () {
      if (CommonUtil.isNotEmpty(this.modelInfo)) {
        if (['LogisticRegression', 'LeastSquare', 'ElasticNet'].indexOf(this.modelInfo.algorithm.algorithmId) >= 0) {
          return this.influenceLinearTitleTip
        }
        if ([
          'DecisionTreeClf',
          'DecisionTreeReg',
          'RandomForestClf',
          'RandomForestReg',
          'GBDTClf',
          'GBDTReg'].indexOf(this.modelInfo.algorithm.algorithmId) >= 0) {
          return this.influenceDecisionTreeTitleTip
        }
      }
      return ''
    }
  },
  created () {
    WebApiClient.getModel(this, this.modelId).then(response => {
      if (response.data.code === Code.SUCCESS) {
        const modelInfo = response.data.result
        this.modelName = modelInfo.modelName
        WebApiClient.getAlgorithmHyperParams(this, modelInfo.algorithm.algorithmId).then(response2 => {
          if (response2.data.code === Code.SUCCESS) {
            let hyperParamSummaries = response2.data.result.hyperParams
            let hyperParams = []

            for (let index in hyperParamSummaries) {
              const hyperParamSummary = hyperParamSummaries[index]
              if (hyperParamSummary.hyperParamId === 'n_units') {
                let nUnits = hyperParamSummaries.splice(Number(index), 1)[0]
                for (let i = 0; i < 3; i++) {
                  hyperParamSummaries.splice(Number(index) + i, 0, Object.assign({}, nUnits, {
                    hyperParamId: `n_units_${i + 1}`,
                    hyperParamName: `${nUnits.hyperParamName} - 隠れ層${['１', '２', '３'][i]}`
                  }))
                }
              }
            }
            for (let hyperParamSummary of hyperParamSummaries) {
              let hyperParam = null
              for (let tmpHyperParam of modelInfo.algorithm.hyperParams) {
                if (hyperParamSummary.hyperParamId === tmpHyperParam.hyperParamId) {
                  hyperParam = tmpHyperParam
                  break
                }
              }
              if (!hyperParam) {
                hyperParam = {
                  hyperParamId: hyperParamSummary.hyperParamId,
                  value: hyperParamSummary.defaultVal
                }
              }
              if (CommonUtil.isEmpty(hyperParam.value)) {
                continue
              } else if (hyperParam.hyperParamId === 'stop_words') {
                hyperParam.value = hyperParam.value.join(',')
              }
              hyperParams.push({
                hyperParam: hyperParam,
                summary: hyperParamSummary
              })
            }

            this.hyperParams = hyperParams
            this.modelInfo = modelInfo
            if (CommonUtil.isNotEmpty(this.modelInfo.figureDetailId)) {
              WebApiClient.getFigure(this, this.modelInfo.figureDetailId)
                .then(this.handleFigureDetailResponse)
                .catch(() => {})
            }

            this.activeTabName = this.hasEvaluations() ? 'evaluations' : (this.hasInfluences() ? 'influences' : 'summary')
            this.loaded = true
            this.$forceUpdate()
          }
        }).catch(() => {})
      }
    }).catch(() => {})
  },
  methods: {
    hasEvaluations () {
      const modelInfo = this.modelInfo
      return (modelInfo.evaluations && modelInfo.evaluations.length > 0) || modelInfo.comparisons
    },
    hasInfluences () {
      const influences = this.modelInfo.influences
      return influences && (influences.importance || influences.importanceX || influences.weight)
    },
    isAllDataModel () {
      if (CommonUtil.isNull(this.modelInfo) || CommonUtil.isNull(this.modelInfo.algorithm.hyperParams)) return false
      for (let hyperParam of this.modelInfo.algorithm.hyperParams) {
        if (hyperParam.hyperParamId === 'update_only_model' && String(hyperParam.value) === '1') {
          return true
        }
      }
      return false
    },
    disabledAllDataModel () {
      if (CommonUtil.isNull(this.modelInfo)) return true
      if (this.modelInfo.samples === this.modelInfo.samplesTrain) return true
      return this.isAllDataModel()
    },
    samplesTrainForAllDataModelTip () {
      return `性能および項目の影響度は、${FormatUtil.formatNumber(this.modelInfo.samplesTrain)}件（${FormatUtil.percentage(
        this.modelInfo.trainRate)}%）で算出`
    },
    onClickConfirmFigure () {
      if (CommonUtil.isEmpty(this.modelInfo.ds.uploadFiles)) {
        MessageBox.alert(this, `学習データが削除されているため、学習データのサマリを作成できません。<br>モデルを更新してから再度実行してください。`, {
          dangerouslyUseHTMLString: true
        }).catch(() => {})
        return
      }

      let categoryId = this.modelInfo.categoryId
      let algorithmId = this.modelInfo.algorithm.algorithmId
      if (CommonUtil.isNotNull(this.modelInfo.remarks) && CommonUtil.isNotEmpty(this.modelInfo.remarks.naviType)) {
        categoryId = Category.NAVI
        algorithmId = this.modelInfo.remarks.naviType
      }
      WebApiClient.postFigures(this, {
        dsName: [this.modelInfo.ds.dsName],
        categoryId: categoryId,
        algorithmId: algorithmId,
        yName: this.modelInfo.yName,
        xNames: this.modelInfo.xName,
        xType: this.modelInfo.xType,
        xCount: this.modelInfo.xName.length,
        isDetail: true
      }, true).then(this.handleFigureDetailResponse).catch(() => {})
    },
    onClickAllDataModel () {
      if (CommonUtil.isEmpty(this.modelInfo.ds.uploadFiles)) {
        MessageBox.alert(this, `学習データが削除されているため、全データモデルを作成できません。<br>モデルを更新してから再度実行してください。`, {
          dangerouslyUseHTMLString: true
        }).catch(() => {})
        return
      }

      WebApiClient.putAllDataModel(this, this.modelId, {
        useAllData: true,
        updatedAt: this.modelInfo.updatedAt
      }).then((response) => {
        const code = response.data.code
        if (code === Code.SUCCESS) {
          this.reload()
        } else if (code === Code.TIMEOUT) {
          this.changeLocation('model-list')
        }
      }).catch(() => {})
    },
    onClickStructureDownload () {
      window.location.href = this.modelInfo.structure
    },
    onClickEvaluationDataDownload () {
      WebApiClient.postEvaluationPreSignedUrl(this, this.modelInfo.remarks.evaluationData).then(response => {
        this.$nextTick(() => {
          const linkEl = document.createElement('a')
          linkEl.href = response.data.result.preSignedUrl
          linkEl.click()
        })
      })
    },
    onClickDelete () {
      const modelName = FormatUtil.escapeHtml(this.modelInfo.modelName)
      const message = `モデル「<b>${modelName}</b>」を削除します<br>(モデルを削除すると適用結果も削除されます)<br><br>削除の確認のため、モデル名を入力：`
      const errorMessage = 'モデル名が違います'
      MessageBox.deleteModelConfirm(this, message, modelName, errorMessage
      ).then(() => {
        this.deleteModel()
      }).catch(() => {})
    },
    onChangeFigurePage (currentPage) {
      this.currentFigurePage = currentPage
      this.$forceUpdate()
    },
    deleteModel () {
      WebApiClient.deleteModel(this, this.modelId).then(response => {
        this.changeLocation('model-list')
      }).catch(() => {})
    },
    handleFigureDetailResponse (response) {
      if (response.data.code === Code.SUCCESS) {
        const figure = response.data.result
        this.modelInfo.figureDetailId = figure.figureId
        this.figureDetail = figure
        this.$forceUpdate()
      }
    },
    formatHyperParamValue (hyperParam) {
      const hyperParamId = hyperParam.hyperParamId
      let value = hyperParam.value
      switch (hyperParamId) {
          case 'regularize':
            value = FormatUtil.capitalize(value)
            break
          case 'kernel':
            value = {
              'linear': 'Linear',
              'poly': 'Poly',
              'rbf': 'RBF',
              'sigmoid': 'Sigmoid'
            }[value]
            break
          case 'boosting_type':
            value = String(value).toUpperCase()
            break
          case 'optimizer':
            value = {
              'adam': 'Adam',
              'adagrad': 'AdaGrad',
              'sgd': 'SGD',
              'momentum': 'Momentum'
            }[value]
            break
          default:
            value = FormatUtil.formatNumber(value)
            break
      }
      return value
    },
    onClickModelUpdate () {
      StorageUtil.removeModelInfo(this)
      if (CommonUtil.isNotEmpty(this.modelInfo.ds.uploadFiles)) {
        this.changeLocation('model-update-confirm', { modelId: this.modelInfo.modelId })
      } else {
        this.changeLocation('model-update-input', { modelId: this.modelInfo.modelId })
      }
    },
    onClickModelPredict () {
      this.$router.push({
        path: `/model/predict/${this.modelId}/`,
        params: {
          modelId: this.modelId,
          modelName: this.modelName
        }
      })
    },
    onClickModelCopy () {
      const modelName = FormatUtil.escapeHtml(this.modelInfo.modelName)
      MessageBox.confirm(this, `モデル「${modelName}」をコピーしますか？`).then(() => {
        this.copyModel()
      }).catch(() => {})
    },
    copyModel () {
      WebApiClient.postModelsCopy(this, this.modelId).then(response => {
        this.changeLocation('model-list')
      }).catch(() => {})
    },
    isMultiClassModel () {
      if (CommonUtil.isEmpty(this.modelInfo.yClasses)) {
        return false
      } else {
        return this.modelInfo.yClasses.length > 2
      }
    },
    onClickYClass () {
      if (this.isMultiClassModel()) {
        this.visibleYClassDialog = true
      }
    },
    getTransformerPackageName (packageId) {
      let packageName = '-'
      if (TRANSFORMER_PACKAGE_MAP.hasOwnProperty(packageId)) {
        packageName = TRANSFORMER_PACKAGE_MAP[packageId]
      }
      return packageName
    }
  }
}


</script>

<style scoped>
  .model-detail-container >>> .main-content {
    padding: 24px;
  }

  .summary-card {
    width: 180px;
    height: 130px;
    border: 1px solid #c0d5ef;
    margin: 8px;
    text-align: center;
    position: relative;
  }

  .summary-card >>> .el-card__body {
    padding: 0.8rem 0.5rem;
  }

  .summary-card-name {
    line-height: normal;
  }

  .summary-card-value {
    padding-top: 0.5rem;
  }

  .summary-card .summary-card-name {
    color: #6ba7ef;
    text-align: center;
  }

  .summary-card .summary-card-tip {
    position: absolute;
    top: 0;
    right: 0;
    font-size: 1.1rem;
    color: #6ba7ef;
  }

  .summary-card .summary-card-value {
    font-size: 0.9rem;
    color: #394445;
  }

  .summary-card .summary-card-value {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 0.9rem;
    color: #394445;
  }

  .influences-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .influences-card {
    width: 964px;
    border: 1px solid #c0d5ef;
    margin: 8px;
  }

  .influences-card .influences-chart-name {
    color: #6ba7ef;
  }

  .influences-card.influences-chart-card >>> .el-card__body {
    padding-top: 40px;
  }

  .influences-card.influences-ranking-card >>> .el-card__body {
    padding-top: 4px;
  }

  .summary-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .summary-hyper-params-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  .figure-card {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 964px;
    height: 360px;
    border: 1px solid #c0d5ef;
    margin: 0 8px 16px 8px;
  }

  .figure-card >>> .el-card__body {
    padding: 0;
    width: 100%;
    height: inherit;
  }

  .figure-acf-wrap-container {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .carousel-container,
  .carousel-container >>> .el-carousel__container {
    padding: 0;
    height: 100%;
  }

  .carousel-label {
    padding: 12px 0 4px;
    text-align: center;
  }

  .influences-chart-card >>> .el-card__body,
  .figure-card >>> .el-card__body {
    position: relative;
  }

  .influences-chart-card >>> .el-card__body > i.zoom,
  .figure-card >>> .el-card__body > i.zoom,
  .summary-card >>> .el-card__body > i.zoom {
    position: absolute;
    top: 6px;
    right: 6px;
    color: #6ba7ef;
    font-size: 1.3rem;
  }

  .main-footer button.delete-button {
    border: solid 1px #d8243e;
    color: #d8243e;
    background-color: #fff;
    flex-grow: 0;
    min-width: auto;
  }

  .main-footer button.delete-button:hover {
    color: #fff;
    border: 1px solid #e88190;
    background: #e88190;
  }

  .main-footer button.delete-button .delete.icon {
    color: #d8243e;
    font-size: 1.4rem;
  }

  .main-footer button.delete-button:hover .delete.icon {
    color: #fff;
  }

  .el-tabs__content {
    width: 964px !important;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/detail.vue